import { RoadieApiError, RoadieApiResponse } from 'roadie-ui'
import {
  ConsolidationCompliance,
  ConsolidationComplianceApiResponse,
  ConsolidationDetails,
  ConsolidationDetailsApiResponse,
  DetailedPayoutBreakdown,
  DetailedPayoutBreakDownApiResponse,
  LocationApiResponse,
  PayoutBreakdown,
  PayoutBreakdownApiResponse,
  RouteInfo,
  RouteInfoApiResponse,
  Sender,
  SenderApiResponse,
  Stop,
  StopApiResponse,
} from 'types/consolidationDetail/consolidation'
import { deserializeGigs } from './gig'
import { getTimezone } from 'util/server/timezone'
import { Coords, GetTimezoneApiResponse } from 'types/timezone'

const defaultConsolidation: ConsolidationDetails = {
  id: 0,
  title: '',
  state: '',
  estimatedDistance: 0,
  totalPayout: 0,
  bearing: 0,
  route: '',
  size: '',
  routeInfo: {
    totalDistance: null,
    totalDriveTime: null,
    totalRoutePolyline: null,
    totalRouteLineString: null,
  },
  stops: [],
  senders: [],
  pickupTime: null,
  pickupWindow: null,
  startTime: null,
  deliveryDeadline: null,
  supportId: null,
  expensesEnabled: false,
  payoutBreakdown: {
    basePayout: 0,
    guaranteedTip: 0,
    cancellationFee: 0,
    netTip: 0,
    seattleAdjustmentPay: 0,
    payoutAdjustment: 0,
  },
  detailedPayoutBreakdown: {
    basePayout: 0,
    guaranteedTip: 0,
    cancellationFee: 0,
    netTip: 0,
    dcaSum: 0,
    senderTips: 0,
  },
  tippingEnabled: false,
  bulkDeliveryEnabled: false,
  gigs: [],
  deliveryCompletedAt: null,
  canceledAt: null,
  friendlyId: null,
  statementUrl: null,
  isSeattle: false,
  bagIds: [],
  idVerificationIntervalMinutes: null,
  pickupTimezone: {
    dstTime: 0,
    rawOffset: 0,
    status: '',
    timeZoneId: '',
    timeZoneName: '',
  },
  deliveryTimezone: {
    dstTime: 0,
    rawOffset: 0,
    status: '',
    timeZoneId: '',
    timeZoneName: '',
  },
}

// deserializer for api response from /consolidations/detail and /consolidations/details
export async function deserializeConsolidationDetail(
  apiResponse: RoadieApiResponse | RoadieApiError
): Promise<ConsolidationDetails> {
  if (('isRoadieApiError' in apiResponse && apiResponse.isRoadieApiError) || !('data' in apiResponse)) {
    return defaultConsolidation
  }

  // determine if consolidation detail by gig ID or singular and get the consolidation data
  let consolidation: ConsolidationDetailsApiResponse
  if (apiResponse.data.consolidations) {
    consolidation = apiResponse.data.consolidations[0]
  } else {
    consolidation = apiResponse.data
  }

  return {
    id: consolidation.id,
    title: consolidation.title,
    state: consolidation.state,
    estimatedDistance: consolidation.estimated_distance,
    totalPayout: consolidation.total_payout,
    bearing: consolidation.bearing,
    route: consolidation.route,
    size: consolidation.size,
    routeInfo: deserializeRouteInfo(consolidation.route_info),
    stops: deserializeStops(consolidation.stops),
    senders: deserializeSenders(consolidation.senders),
    pickupTime: consolidation.pickup_time ? new Date(consolidation.pickup_time) : null,
    pickupTimezone: await getLocationTimezone(
      consolidation.stops[0]?.location,
      consolidation.pickup_time ? new Date(consolidation.pickup_time) : new Date()
    ),
    pickupWindow: consolidation.pickup_window || defaultConsolidation.pickupWindow,
    startTime: consolidation.start_time ? new Date(consolidation.start_time) : null,
    deliveryDeadline: consolidation.delivery_deadline ? new Date(consolidation.delivery_deadline) : null,
    deliveryTimezone: await getLocationTimezone(
      consolidation.stops[consolidation.stops.length - 1].location,
      consolidation.delivery_deadline ? new Date(consolidation.delivery_deadline) : new Date()
    ),
    supportId: consolidation.support_id || null,
    expensesEnabled: consolidation.expenses_enabled,
    payoutBreakdown: deserializePayoutBreakdown(consolidation.payout_breakdown),
    detailedPayoutBreakdown: deserializeDetailedPayoutBreakdown(consolidation.detailed_payout_breakdown),
    tippingEnabled: consolidation.tipping_enabled,
    bulkDeliveryEnabled: consolidation.bulk_delivery_enabled,
    gigs: deserializeGigs(consolidation.gigs),
    deliveryCompletedAt: consolidation.delivery_completed_at
      ? new Date(
          typeof consolidation.delivery_completed_at === 'number'
            ? consolidation.delivery_completed_at * 1000
            : consolidation.delivery_completed_at
        )
      : null,
    canceledAt: consolidation.canceled_at
      ? new Date(
          typeof consolidation.canceled_at === 'number' ? consolidation.canceled_at * 1000 : consolidation.canceled_at
        )
      : null,
    friendlyId: consolidation.friendly_id || '',
    statementUrl: consolidation.statement_url,
    isSeattle: consolidation.is_seattle || false,
    bagIds: consolidation.bag_ids || [],
    idVerificationIntervalMinutes: consolidation.id_verification_interval_minutes || 0,
  }
}

function deserializeRouteInfo(info: RouteInfoApiResponse | null | undefined): RouteInfo {
  if (!info) {
    return defaultConsolidation.routeInfo
  }

  return {
    totalDistance: info.total_distance,
    totalDriveTime: info.total_drive_time,
    totalRoutePolyline: info.total_route_polyline,
    totalRouteLineString: info.total_route_line_string,
  }
}

function deserializeStops(stops: StopApiResponse[]): Stop[] {
  if (!stops) {
    return []
  }

  const cStops: Stop[] = []
  for (const s of stops) {
    cStops.push({
      index: s.index,
      gigIds: s.gig_ids || [],
      category: s.category,
      lastEvent: s.last_event,
      signatureRequired: s.signature_required || false,
      estimatedStopTime: s.estimated_stop_time,
      location: {
        latitude: s.location?.latitude,
        longitude: s.location?.longitude,
        city: s.location?.city,
        state: s.location?.state,
        zip: s.location?.zip,
        friendlyName: s.location?.friendly_name,
      },
      automatedReturnGig: s.automated_return_gig,
      automatedPickupGig: s.automated_pickup_gig,
      eta: s.eta,
    })
  }

  return cStops
}

function deserializeSenders(senders: SenderApiResponse[]): Sender[] {
  if (!senders) {
    return []
  }

  const cSenders: Sender[] = []
  for (const s of senders) {
    cSenders.push({
      id: s.id,
      name: s.name,
      avatarUrl: s.avatar_url,
      verifiedAvatarUrl: s.verified_avatar_url,
      settings: {
        latestDeliveryTime: s.settings?.latest_delivery_time,
      },
    })
  }

  return cSenders
}

function deserializePayoutBreakdown(breakdown: PayoutBreakdownApiResponse): PayoutBreakdown {
  return {
    seattleAdjustmentPay: breakdown.seattle_adjustment_pay,
    payoutAdjustment: breakdown.payout_adjustment,
    basePayout: breakdown.base_payout,
    guaranteedTip: breakdown.guaranteed_tip,
    cancellationFee: breakdown.cancellation_fee,
    netTip: breakdown.net_tip,
  }
}

function deserializeDetailedPayoutBreakdown(breakdown: DetailedPayoutBreakDownApiResponse): DetailedPayoutBreakdown {
  return {
    dcaSum: breakdown.dca_sum,
    senderTips: breakdown.sender_tips,
    basePayout: breakdown.base_payout,
    guaranteedTip: breakdown.guaranteed_tip,
    cancellationFee: breakdown.cancellation_fee,
    netTip: breakdown.net_tip,
  }
}

async function getLocationTimezone(location: LocationApiResponse, time: Date): Promise<GetTimezoneApiResponse | null> {
  const coords: Coords = {
    latitude: location?.latitude?.toString(),
    longitude: location?.longitude?.toString(),
  }
  const pickupDateTime = new Date(time) || new Date()

  return await getTimezone(coords, pickupDateTime)
}

const defaultCompliance: ConsolidationCompliance = {
  totalDistanceMiles: 0,
  totalTimeWorkedSeconds: 0,
  totalEstimatedDistanceMiles: 0,
  totalEstimatedTimeMinutes: 0,
}

export function deserializeConsolidationCompliance(
  apiResponse: RoadieApiResponse | RoadieApiError
): ConsolidationCompliance {
  if (('isRoadieApiError' in apiResponse && apiResponse.isRoadieApiError) || !('data' in apiResponse)) {
    return defaultCompliance
  }

  const complianceData: ConsolidationComplianceApiResponse = apiResponse.data
  return {
    totalDistanceMiles: complianceData.total_distance_miles,
    totalTimeWorkedSeconds: complianceData.total_time_worked_seconds,
    totalEstimatedDistanceMiles: complianceData.total_estimated_distance_miles,
    totalEstimatedTimeMinutes: complianceData.total_estimated_time_minutes,
  }
}
